/* eslint-disable no-magic-numbers -- surface tokens are named by number */
import styled from 'styled-components';
import { mainTheme } from '../themes/mainTheme';
import React from 'react';
import { withCssSelector } from '../shared/withCssSelector';
import { LabelVariant, LabelVariantCssDefinition } from './LabelVariant';
import { Icon, IconName } from '../icon';

// Strong chips: the vivid 500 step with inverted text on top.
const strongColors: Record<LabelVariant, { bg: string; text: string }> = {
	neutral: { bg: mainTheme.fill.interactive, text: mainTheme.text.inverted },
	success: { bg: mainTheme.border.success, text: mainTheme.text.inverted },
	warning: { bg: mainTheme.border.warning, text: mainTheme.text.inverted },
	critical: { bg: mainTheme.border.danger, text: mainTheme.text.inverted },
	informational: { bg: mainTheme.border.info, text: mainTheme.text.primary },
	secondary: { bg: mainTheme.surface[4], text: mainTheme.text.primary },
};

const sizeStyles = {
	default: {
		height: '20px',
		padding: '4px 6px',
		fontSize: '12px',
		icon: '14px',
	},
	large: {
		height: '28px',
		padding: '4px 10px',
		fontSize: '14px',
		icon: '16px',
	},
};

const StyledSpan = styled.span<{
	$variant: LabelVariant;
	$rounded?: boolean;
	$darkColor?: boolean;
	$size?: 'default' | 'large';
}>`
	height: ${props => sizeStyles[props.$size || 'default'].height};
	padding: ${props => sizeStyles[props.$size || 'default'].padding};
	border-radius: ${props => props.$rounded ? '18px' : '2px'};
	width: fit-content;
	display: flex;
	align-items: center;
	gap: 4px;

	text-align: center;
	font-size: ${props => sizeStyles[props.$size || 'default'].fontSize};
	font-style: normal;
	font-weight: 400;
	line-height: 16px;

	.icon {
		font-size: ${props => sizeStyles[props.$size || 'default'].icon};
	}

	${props => LabelVariantCssDefinition[props.$variant]}
	${props => props.$darkColor ? `
		background-color: ${strongColors[props.$variant].bg};
		color: ${strongColors[props.$variant].text};
	` : ''}
`;

const defaultIcons: Record<LabelVariant, IconName> = {
	neutral: 'circle-check-outline_minor',
	success: 'circle-check-outline_minor',
	warning: 'warning-new',
	critical: 'exclamation-circle-outline',
	informational: 'info-icon-new',
	secondary: 'info-icon-new',
};

export type LabelProps = {
	variant?: LabelVariant | undefined;
	icon?: IconName;
	rounded?: boolean;
	number?: number;
	useDefaultIcon?: boolean;
	darkColor?: boolean;
	size?: 'default' | 'large';
} & React.ComponentPropsWithoutRef<'span'>

export const Label = withCssSelector(React.forwardRef<HTMLSpanElement, LabelProps>(
	function Label({ variant = 
	'neutral', icon, rounded = true, number, useDefaultIcon, darkColor, size = 'large', ...props }, ref) {
		// If number is provided, ignore icon
		const displayIcon = number === undefined 
			? (icon || (useDefaultIcon ? defaultIcons[variant] : undefined))
			: undefined;

		return (
			<StyledSpan $variant={variant} $rounded={rounded} $darkColor={darkColor} $size={size} {...props} ref={ref}>
				{displayIcon && <Icon icon={displayIcon} />}
				{number !== undefined ? number : props.children}
			</StyledSpan>
		);
	}
), StyledSpan.toString());
